import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Markdown } from '../../components/Markdown.js'

interface TaskDescriptionFieldProps {
  description: string | null
  onSave: (description: string) => void
}

/**
 * Edición en línea de la descripción en markdown (CLAUDE.md §6). El modo
 * lectura es un `div` con `role="button"`, no un `<button>`: el markdown
 * puede traer enlaces, e HTML no permite contenido interactivo dentro de un
 * `<button>`.
 */
export function TaskDescriptionField({ description, onSave }: TaskDescriptionFieldProps) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(description ?? '')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (editing) textareaRef.current?.focus()
  }, [editing])

  function startEditing(): void {
    setValue(description ?? '')
    setEditing(true)
  }

  function commit(): void {
    setEditing(false)
    const trimmed = value.trim()
    if (trimmed !== (description ?? '')) onSave(trimmed)
  }

  if (editing) {
    return (
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            setValue(description ?? '')
            setEditing(false)
          }
        }}
        rows={6}
        placeholder={t('task.detail.descriptionPlaceholder')}
        className="-mx-2 w-[calc(100%+16px)] resize-y rounded-card bg-surface-hi px-2 py-2 text-body text-text outline-none focus-visible:ring-2 focus-visible:ring-accent"
      />
    )
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={startEditing}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          startEditing()
        }
      }}
      className="-mx-2 w-[calc(100%+16px)] cursor-text rounded-card px-2 py-2 hover:bg-surface-hi focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
    >
      {description ? (
        <Markdown>{description}</Markdown>
      ) : (
        <span className="text-body text-text-muted">{t('task.detail.noDescription')}</span>
      )}
    </div>
  )
}
